import {
  Box,
  Stack,
  Skeleton,
  Alert,
  Typography,
  Divider,
  LinearProgress,
} from '@mui/material';
import { usePlotDetails } from '../../../hooks/usePlotDetails';
import { useSitePanelState } from '../../../hooks/useSitePanelState';
import { InfoRow } from '../atoms/InfoRow';
import { PagedActivityTimeline } from '../molecules/PagedActivityTimeline';
import { usePlotEventsTimeline } from '../../../hooks/usePlotEventsTimeline';
import { formatCurrency } from '../../../../../shared/utils/currency';

interface PlotDetailsPanelProps {
  plotId: number;
}

export function PlotDetailsPanel({ plotId }: PlotDetailsPanelProps) {
  const { siteId } = useSitePanelState();
  const siteIdNum = siteId ? parseInt(siteId, 10) : null;
  const { data: plot, error } = usePlotDetails(siteIdNum, plotId);

  const {
    events,
    totalCount: timelineTotalCount,
    hasMore: timelineHasMore,
    isLoading: timelineLoading,
    isLoadingMore: timelineLoadingMore,
    loadMore: timelineLoadMore,
  } = usePlotEventsTimeline(siteIdNum, plotId);

  if (error && !plot) {
    return (
      <Box data-testid="plot-details-panel" sx={{ p: 3 }}>
        <Alert severity="error">
          {error instanceof Error
            ? error.message
            : 'Failed to load plot details'}
        </Alert>
      </Box>
    );
  }

  if (!plot) {
    return (
      <Box data-testid="plot-details-panel" sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Skeleton variant="rectangular" height={80} />
          <Skeleton variant="rectangular" height={120} />
          <Skeleton variant="rectangular" height={300} />
        </Stack>
      </Box>
    );
  }

  const { financialMetrics } = plot;

  return (
    <Box data-testid="plot-details-panel">
      <Box sx={{ p: 3, pb: 0 }}>
        <Typography variant="h6" sx={{ lineHeight: 1.3 }}>
          {plot.name}
        </Typography>
      </Box>

      <Box sx={{ p: 3 }}>
        <Stack spacing={0}>
          <Typography
            variant="overline"
            color="text.primary"
            sx={{ display: 'block', mb: 1, fontWeight: 600 }}
          >
            Details
          </Typography>
          <InfoRow label="Plot Number" testId="plot-number">
            {plot.plotNumber}
          </InfoRow>
          <InfoRow label="Labour Items" testId="labour-item-count">
            {plot.labourItemCount}
          </InfoRow>

          {financialMetrics && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography
                variant="overline"
                color="text.primary"
                sx={{ display: 'block', mb: 1, fontWeight: 600 }}
              >
                Financials
              </Typography>
              <InfoRow label="Total Budget" testId="total-budget">
                {formatCurrency(financialMetrics.totalLabourPrice)}
              </InfoRow>
              <InfoRow label="Total Spent" testId="total-spent">
                {formatCurrency(financialMetrics.totalSpent)}
              </InfoRow>
              <InfoRow label="Remaining" testId="remaining">
                <Typography
                  component="span"
                  variant="body2"
                  sx={{
                    color:
                      financialMetrics.remaining >= 0
                        ? 'success.main'
                        : 'error.main',
                  }}
                >
                  {formatCurrency(financialMetrics.remaining)}
                </Typography>
              </InfoRow>
              <Box sx={{ mb: 1.5 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 0.5,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Progress
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {financialMetrics.progress.toFixed(1)}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(financialMetrics.progress, 100)}
                  sx={{
                    height: 8,
                    borderRadius: 1,
                    backgroundColor: 'action.hover',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 1,
                      backgroundColor:
                        financialMetrics.progress > 100
                          ? 'error.main'
                          : 'primary.main',
                    },
                  }}
                />
              </Box>
            </>
          )}

          <Divider sx={{ my: 2 }} />
          <Typography
            variant="overline"
            color="text.primary"
            sx={{ display: 'block', mb: 1.5, fontWeight: 600 }}
          >
            Activity
          </Typography>
          <PagedActivityTimeline
            events={events}
            totalCount={timelineTotalCount}
            hasMore={timelineHasMore}
            isLoading={timelineLoading}
            isLoadingMore={timelineLoadingMore}
            loadMore={timelineLoadMore}
          />
        </Stack>
      </Box>
    </Box>
  );
}
